import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

import { getTimezoneShort } from 'utils/time';
import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import LocationIcon from 'assets/images/calendar/icon-location.svg';
import NotesIcon from 'assets/images/calendar/icon-notes.svg';
import StatusIcon from 'assets/images/calendar/icon-status.svg';
import VideoCallIcon from 'assets/images/calendar/icon-video-call.svg';
import { EventDateTimeDisplay } from './EventDateTimeDisplay';

dayjs.extend(timezone);

const ExternalEventCard = ({ eventDetails, userDetails }) => {
  const { title, startStr, endStr, extendedProps } = eventDetails || {};
  const {
    whenType,
    conferencingUrl,
    busy,
    location,
    notes = '',
    externalCalendarId,
    htmlLink,
  } = extendedProps || {};
  const { timeZone, timeFormat, loginCalendarsById = {}, integration } = userDetails || {};
  const { provider } = integration || {};
  const start = dayjs(startStr).tz(timeZone);
  const end = dayjs(endStr).tz(timeZone);
  const sameDay = useMemo(() => {
    if (whenType === 'timespan') return start.isSame(end, 'day');
    // For all day events, end time is the next day but at 00:00:00, so subtract
    // 1 day to see if it's the same day.
    return end.subtract(1, 'day').isSame(start, 'day');
  }, [whenType]);
  const calendarName = loginCalendarsById[externalCalendarId]?.name || '';
  const shortTz = useMemo(() => getTimezoneShort(timeZone, new Date(start.valueOf())), [timeZone]);

  // #TODO: Events from Microsoft can have a description built by a fully featured wysiwyg.
  // Will need to likely strip out some tags so the description doesn't get blown up in the app.
  // Depending on the source, the notes/description of an event can be a html string.
  const sanitizedNotes = parse(
    DOMPurify.sanitize(
      notes,
      {
        FORBID_ATTR: ['style'],
        FORBID_TAGS: ['img', 'table'],
      },
    ),
    { transform: transformExternalLinks },
  );

  return (
    <div className="event-card info-card event-card--external">
      <div className="info-card__content">
        <div className="info-card__row">
          <div className="info-card__title ap-text">
            {title}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            <EventDateTimeDisplay
              start={start}
              end={end}
              whenType={whenType}
              timeFormat={timeFormat}
              shortTz={shortTz}
              sameDay={sameDay}
            />
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={VideoCallIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {conferencingUrl ? (
              <a
                href={conferencingUrl}
                target="_blank"
                rel="noreferrer nofollow"
                className="ap-text--sm gold"
              >
                Join video call
              </a>
            ) : (
              '-'
            )}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={StatusIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {busy ? 'Busy' : 'Free'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LocationIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {location || '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={NotesIcon}
              alt="date-time icon"
            />
          </div>
          <div className="info-card__description">
            {notes ? sanitizedNotes : '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {calendarName}
          </div>
        </div>
      </div>

      {htmlLink && (
        <a
          href={htmlLink}
          className="button external-event-card__button"
          target="_blank"
          rel="noreferrer nofollow"
        >
          View in {provider || 'External Calendar'}
        </a>
      )}

    </div>
  );
};

/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable-next-line no-unused-vars */
const transformExternalLinks = (reactNode, domNode, index) => {
  const { props, type } = reactNode;
  if (type === 'a') {
    return (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
      />
    );
  }
  return reactNode;
};
/* eslint-enable jsx-a11y/anchor-has-content */

export default ExternalEventCard;
